/**
 * 站点卡片上，Logo 要不要垫一块底板。
 *
 * 背景：卡片封面是一条固定的深色渐变（品牌色可能本身是白的，白底白字等于没字，
 * 所以封面不敢用品牌色兜底）。深色封面带来一个必须回答的问题 ——
 * **客户的 Logo 是给什么底色设计的？**
 *
 * 传上来的 Logo 有三种，外观上分不出来（都是"一张 PNG"）：
 *   1. 透明底 + 深色图形 —— 外贸站默认白底，这种要垫白板，否则落在深色封面上消失；
 *   2. 透明底 + 白色/浅色图形 —— 官网本来就是深色页头，这种**垫白板反而消失**
 *      （截图里那块"颜色不对的底板"就是它：白标压在白板上，看上去是一块空板子）；
 *   3. 自带底色（白底图/深底图）—— 它自己就是一块卡，垫不垫都看得见。
 *
 * 所以判据不是"平均色"一种，而是两问：
 *   · 四角是实心的吗（自带底色）？是的话按那个底色挑边：浅底垫白板把边收进圆角，
 *     深底直接压在封面上（边缘本来就融在一起）。
 *   · 四角透明的话，看**不透明像素的平均亮度**：偏亮=为深底画的不垫，偏暗=垫白板。
 *
 * 读像素失败（COS 没开 CORS、图片加载失败）一律**退回垫白板**：深色 Logo 是
 * 大多数，猜错的代价只是"浅色 Logo 看不见"这一种，而不垫板猜错会让深色 Logo
 * 完全消失。
 */

export interface InkStats {
  /** 不透明像素的平均亮度（0–1，按 alpha 加权） */
  meanLuma: number;
  /** 四角底色亮度；四角透明（或读不到）时为 null，表示 Logo 是透明底 */
  bgLuma: number | null;
}

/**
 * 由像素统计决定底板。抽成纯函数是为了能直接断言阈值，
 * 不必为了测三个分支去真造三张图。
 */
export function plateForInk(stats: InkStats | null): 'none' | 'white' {
  if (!stats) return 'white';
  /**
   * 自带底色：按底色挑。
   * 浅底必须垫白板 —— 否则深色封面上多出一块硬边的白方块，看着像贴纸；
   * 深底不垫 —— 白板会把深底标围成一圈白框，更难看。
   */
  if (stats.bgLuma !== null) return stats.bgLuma >= 0.5 ? 'white' : 'none';
  /**
   * 透明底：0.6 这条线卡在"白字标"（约 0.8–1.0）与"深色标"（约 0.1–0.35）
   * 中间。品牌色标（橙、蓝）落在 0.4–0.6 之间，两边都不难看，按白板处理 ——
   * 垫板猜错只是难看，不垫板猜错是消失。
   */
  return stats.meanLuma >= 0.6 ? 'none' : 'white';
}

/** url → 判定结果。卡片会随站点列表反复重挂，没必要每次都重读一遍像素 */
const cache = new Map<string, 'none' | 'white'>();

/** 同步读缓存：首帧就能给出正确底板，避免"先白板、量完又消失"闪一下 */
export function cachedPlate(url: string | null): 'none' | 'white' | null {
  if (!url) return null;
  return cache.get(url) ?? null;
}

/**
 * 读一张图的像素统计。
 *
 * `crossOrigin` 必须显式设：COS 的图片带 `Access-Control-Allow-Origin`
 * （控制台域名在白名单里），但**不设这个属性浏览器不会带 Origin**，
 * 画进 canvas 后再 `getImageData` 会抛 SecurityError。
 * 设了之后若对方没开 CORS，图会直接加载失败 —— 两种失败都走同一个兜底。
 */
export async function measureLogoInk(url: string, size = 48): Promise<InkStats | null> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('这张图读不出来'));
      image.src = url;
    });
  } catch {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(image, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    const lumaAt = (x: number, y: number): number => {
      const i = (y * size + x) * 4;
      return (0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!) / 255;
    };

    let weighted = 0;
    let weight = 0;
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const alpha = data[(y * size + x) * 4 + 3]!;
        if (alpha < 16) continue;
        // 按 alpha 加权：抗锯齿边缘的半透明像素亮度不真实，直接算会把细字标拉偏
        weighted += lumaAt(x, y) * alpha;
        weight += alpha;
      }
    }
    if (weight === 0) return null;

    /**
     * 四角是不是实心，决定"自带底色"还是"透明底"。
     * 取 2×2 的小方块再平均：只取一个像素会被压缩噪点骗到（浅底图边缘常有一像素灰边）。
     */
    const corner = (cx: number, cy: number): number | null => {
      let sum = 0;
      let count = 0;
      for (let dx = 0; dx < 2; dx += 1) {
        for (let dy = 0; dy < 2; dy += 1) {
          const x = Math.min(size - 1, cx + dx);
          const y = Math.min(size - 1, cy + dy);
          const i = (y * size + x) * 4;
          if (data[i + 3]! < 240) return null;
          sum += lumaAt(x, y);
          count += 1;
        }
      }
      return count > 0 ? sum / count : null;
    };
    const corners = [
      corner(0, 0),
      corner(size - 2, 0),
      corner(0, size - 2),
      corner(size - 2, size - 2),
    ];
    const bgLuma =
      corners.every((value): value is number => value !== null)
        ? corners.reduce((sum, value) => sum + value, 0) / corners.length
        : null;

    return { meanLuma: weighted / weight, bgLuma };
  } catch {
    // 跨域被拒（CORS 没放开）时走这里
    return null;
  }
}

/**
 * 量一次并记住结果。失败也记 —— 反正是"垫白板"，重试不会变好，
 * 而每张卡片每次挂载都去拉一次图会拖慢列表。
 */
export async function resolveLogoPlate(url: string | null): Promise<'none' | 'white' | null> {
  if (!url) return null;
  const hit = cache.get(url);
  if (hit) return hit;
  const stats = await measureLogoInk(url);
  const plate = plateForInk(stats);
  cache.set(url, plate);
  return plate;
}
